import { GetAllExpeditionResponseDtoExpedition } from "@/types/api/api-types";
import { useEffect } from "react";
import { format, formatDate } from "date-fns";
import { fr } from "date-fns/locale";
import { StyledMainActionButton } from "@/components/styled/StyledMainActionButton";
import { StyledSecondaryActionButton } from "@/components/styled/StyledSecondaryActionButton";
import { FileSpreadsheet, X } from "lucide-react";
import { ActionData } from "@/types/tableAction";
import { CountryBadge } from "@/components/shared/badge/country.badge";
import { Button } from "@/components/ui/button";
import { handleOpenDocument } from "@/lib/file.util";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { DeliveryStatusBadge } from "@/components/shared/badge/deliveryStatus.badge";
import { getExpeditionIdentifier } from "@/lib/expedition.util";

type Props = {
  expedition: GetAllExpeditionResponseDtoExpedition;
  onClose: () => void;
  navigate: (path: string) => void;
  setActionData: (actionData: ActionData) => void;
};

export const ValidatedExpeditionMobileModal = ({
  expedition,
  onClose,
  navigate,
  setActionData,
}: Props) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const expeditionIdentifier = getExpeditionIdentifier(expedition);

  const client =
    expedition.deliveryInfo.company ||
    (expedition.deliveryInfo.firstName && expedition.deliveryInfo.lastName
      ? expedition.deliveryInfo.firstName + " " + expedition.deliveryInfo.lastName
      : "-");

  const quote = expedition.expeditionDocuments?.documents.find(
    (document) => document.type === FileTypeEnum.EXPEDITION_QUOTE,
  );

  const handleModifyCotation = () => {
    if (expedition.deliveryStatus !== DeliveryStatusEnum.WAITING) {
      navigate(`/update-request/${expedition.id}`);
    } else {
      if (expedition.quotation) {
        if (expedition.quotation.viaImportedInvoice) {
          navigate(`/update-cotation/ocr/${expedition.id}`);
        } else {
          navigate(`/update-cotation/${expedition.id}`);
        }
      } else {
        navigate(`/update-request/${expedition.id}`);
      }
    }
  };

  const canBePostponed =
    expedition.pickupDate &&
    new Date(expedition.pickupDate) > new Date() &&
    expedition.deliveryStatus === DeliveryStatusEnum.WAITING;

  const handlePostpone = () => {
    if (canBePostponed) {
      setActionData({
        expedition: expedition,
        action: "POSTPONE",
      });
    }
  };

  return (
    <dialog className="fixed top-0 left-0 z-50 flex h-full w-full items-end justify-center overflow-auto bg-[#00000080] backdrop-blur-sm">
      <div className="flex w-full flex-col items-center justify-center gap-2 rounded-t-2xl bg-white p-6">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="w-[20px]"></div>
          <span className="text-sm font-medium text-gray-600">
            Expedition {expeditionIdentifier}
          </span>
          <button onClick={onClose} className="text-sm font-medium text-black">
            <X />
          </button>
        </div>
        <div className="flex w-full flex-row items-center justify-center gap-2">
          <span className="max-w-[150px] truncate text-sm font-medium text-black">{client}</span>
          <span className="text-xs text-gray-600">
            {formatDate(expedition.createdAt, "dd MMM yyyy", {
              locale: fr,
            })}
          </span>
          <DeliveryStatusBadge
            type={expedition.deliveryStatus as DeliveryStatusEnum}
            isBo={false}
          />
        </div>
        <div className="flex w-full flex-row items-center justify-center gap-2">
          <p className="max-w-[200px] overflow-hidden text-left text-sm font-medium text-ellipsis text-gray-600">
            Date d'enlèvement
          </p>
          {expedition.pickupDate ? (
            new Date(expedition.pickupDate) > new Date() &&
            expedition.deliveryStatus === "WAITING" ? (
              <span className="justify-left flex w-fit text-left text-sm font-medium text-blue-700 hover:text-blue-500">
                {format(expedition.pickupDate, "dd MMM yyyy", {
                  locale: fr,
                })}
              </span>
            ) : (
              <span className="w-fit text-left font-medium">
                {format(expedition.pickupDate, "dd MMM yyyy", {
                  locale: fr,
                })}
              </span>
            )
          ) : (
            <div className="justify-left flex w-fit">
              <div className="py-.5 w-fit rounded-md bg-gray-500 px-2">
                <span className="w-full text-center text-sm font-medium text-white">
                  Non définie
                </span>
              </div>
            </div>
          )}
        </div>
        {expedition.deliveryStatus === "DELIVERED" && expedition.deliveryDate && (
          <div className="flex w-full flex-row items-center justify-center gap-2">
            <p className="max-w-[200px] overflow-hidden text-left text-sm font-medium text-ellipsis text-gray-600">
              Date de livraison
            </p>
            <span className="w-fit text-left font-medium">
              {format(expedition.deliveryDate, "dd MMM yyyy", {
                locale: fr,
              })}
            </span>
          </div>
        )}
        {expedition.customQuotation ? (
          quote ? (
            <Button variant="outline" onClick={() => handleOpenDocument(quote.path)}>
              <FileSpreadsheet />
              <p>{quote.name}</p>
            </Button>
          ) : (
            <Button disabled={true} variant="outline" onClick={() => {}}>
              <FileSpreadsheet />
              <p>Devis en cours</p>
            </Button>
          )
        ) : (
          <></>
        )}
        <CountryBadge country={expedition.deliveryInfo?.country || "-"} />
        <div className="flex w-full flex-row items-center justify-center gap-4 pt-3">
          <StyledSecondaryActionButton
            onClick={handleModifyCotation}
            text="Modifier"
            isLoading={false}
          />
          <StyledMainActionButton
            onClick={() =>
              setActionData({
                expedition: expedition,
                action: "DETAIL",
              })
            }
            text="Détails"
            isLoading={false}
          />
        </div>
        <button
          onClick={handlePostpone}
          disabled={!canBePostponed}
          className={`text-brand700 h-fit w-fit bg-transparent p-2 font-semibold focus:outline-hidden ${
            !canBePostponed ? "text-gray-400" : ""
          }`}
        >
          Reporter l'enlèvement
        </button>
        <button
          onClick={() =>
            setActionData({
              expedition: expedition,
              action: "DELETE",
            })
          }
          className="h-fit w-fit bg-transparent p-2 font-semibold text-red-600 focus:outline-hidden"
        >
          {expedition.deletedRequestAt ? "Annuler la suppression" : "Supprimer"}
        </button>
      </div>
    </dialog>
  );
};
